// Helper central do Meta Pixel (Facebook/Instagram Ads).
//
// Objetivo: concentrar toda chamada a `window.fbq` num único lugar, protegida
// contra os cenários em que o pixel pode não estar disponível:
//   - REACT_APP_META_PIXEL_ID não configurado (dono ainda não passou o ID)
//   - bloqueador de anúncios / navegador bloqueando o script do Facebook
//   - script ainda não carregou (race condition no primeiro render)
//
// Nunca deixe uma chamada de `fbq(...)` solta em componentes — sempre passe
// por uma das funções abaixo.
//
// LGPD: os eventos disparados aqui NÃO devem carregar dados pessoais
// (nome, e-mail, CPF, telefone). Apenas metadados de conversão como
// `value` e `currency`.

const PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID || '';

/** Pixel configurado (ID presente). Não garante que o script carregou. */
export const isPixelConfigured = () => Boolean(PIXEL_ID);

/** window.fbq existe e está pronto para receber chamadas. */
function fbqReady() {
  return typeof window !== 'undefined' && typeof window.fbq === 'function';
}

/**
 * Injeta o script base do Meta Pixel (fbevents.js) e faz o `init`.
 * Chamado uma única vez no bootstrap do app (ver src/index.js).
 * Se não houver ID configurado, não faz nada — nenhum script é carregado.
 */
export function initMetaPixel() {
  if (!PIXEL_ID) return;
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (fbqReady()) return; // já inicializado (ex.: StrictMode duplo mount)

  try {
    /* eslint-disable */
    !(function (f, b, e, v, n, t, s) {
      if (f.fbq) return;
      n = f.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = '2.0';
      n.queue = [];
      t = b.createElement(e);
      t.async = true;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
    /* eslint-enable */

    window.fbq('init', PIXEL_ID);
  } catch {
    // Falha silenciosa — anúncio/pixel nunca pode derrubar o app.
  }
}

/** PageView — deve ser chamado a cada troca de rota da SPA. */
export function trackPageView() {
  if (!fbqReady()) return;
  try {
    window.fbq('track', 'PageView');
  } catch {
    // silencioso
  }
}

/**
 * CompleteRegistration — cadastro concluído com sucesso (email/senha ou Google).
 * Sem dados pessoais: nenhum e-mail/nome é enviado.
 */
export function trackCompleteRegistration() {
  if (!fbqReady()) return;
  try {
    window.fbq('track', 'CompleteRegistration');
  } catch {
    // silencioso
  }
}

/**
 * Subscribe — conversão principal: assinatura paga confirmada.
 * @param {Object} params
 * @param {number} params.value - valor do plano (ex.: 49.90)
 * @param {string} [params.currency] - default 'BRL'
 */
export function trackSubscribe({ value, currency = 'BRL' } = {}) {
  if (!fbqReady()) return;
  try {
    const payload = {};
    if (typeof value === 'number' && !Number.isNaN(value)) {
      payload.value = value;
      payload.currency = currency;
    }
    window.fbq('track', 'Subscribe', payload);
  } catch {
    // silencioso
  }
}
